<template>
    <header class="header flex flex-col relative">
        <!-- ── 主导航栏 ── -->
        <div class="header_top flex items-center justify-between">
            <div class="draggable absolute" id="draggable" @mousedown="appStore.MyWin.startDragWindow()"></div>
            <!-- Logo -->
            <div class="header_logo flex items-center pointer" @click="appStore.goHome()">
                <img class="logo" src="@/assets/global/Logo_office.png" alt="office办公">
                <p class="logo_text f18">office办公</p>
            </div>

            <!-- 中间搜索栏 - 暂时隐藏 -->
            <!-- <div class="hn_search_wrap relative flex items-center">
                <input id="searchInput" v-model="searchKeyword" class="hn_search_input" type="text"
                    placeholder="搜索产品、激活码、插件…" autocomplete="off" @keydown.enter="handleSearch" />
                <span class="hn_search_ico flex items-center">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                        stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                </span>
            </div> -->

            <!-- 右侧：登录/头像 + 窗口控制 -->
            <div class="header_right flex items-center">
                <!-- 未登录：登录按钮 -->
                <button v-if="!appStore.UserInfo?.token" class="hn_login_btn flex items-center pointer"
                    @click="appStore.openLogin()">
                    🔒 <span>登录</span>
                </button>
                <!-- 已登录：头像 + 下拉 -->
                <div v-else ref="avatarButtonRef" class="hn_avatar_wrap flex items-center pointer" title="我的账户"
                    @click="appStore.toggleAvatarMenu()">
                    <div class="hn_avatar flex-center">{{ appStore.avatarLabel }}</div>
                    <span class="hn_avatar_caret">▾</span>
                </div>

                <!-- 窗口控制 -->
                <div class="hn_win_sep"></div>
                <button class="hn_win_btn flex-center pointer" title="最小化" @click="appStore.MyWin.minimizeWindow()">
                    <img class="btn_icon" src="@/assets/global/minSize.svg" alt="">
                </button>
                <button class="hn_win_btn flex-center pointer" :title="appStore.isMaxSize ? '恢复' : '最大化'"
                    @click="appStore.MyWin.maximizeWindow()">
                    <img v-show="!appStore.isMaxSize" class="btn_icon" src="@/assets/global/maxSize.svg" alt="">
                    <img v-show="appStore.isMaxSize" class="btn_icon" src="@/assets/global/maxSize_reset.svg" alt="">
                </button>
                <button class="hn_win_btn close flex-center pointer" title="关闭" @click="appStore.MyWin.close()">
                    <img class="btn_icon" src="@/assets/global/close.svg" alt="">
                </button>
            </div>
        </div>

        <!-- ── 副导航栏（面包屑） ── -->
        <div class="header_sub flex items-center">
            <div class="header_breadcrumb flex items-center">
                <span class="hbc_root pointer" @click="appStore.goHome()">office办公</span>
                <span class="hbc_sep">›</span>
                <span class="hbc_current">{{ breadcrumbTitle }}</span>
            </div>
        </div>

        <!-- 登录后账户下拉菜单 -->
        <div id="avatarMenu" ref="avatarMenuRef" class="avatar_menu absolute overflow-hidden"
            :class="{ show: appStore.avatarMenuVisible }">
            <div class="avatar_menu_header">
                <span class="wc_ico flex-center f13 b">{{ appStore.avatarLabel }}</span>
                <div>
                    <div class="name">{{ appStore.UserInfo?.nickname }}</div>
                    <div class="sub">✓ 微信已登录</div>
                </div>
            </div>
            <div class="avatar_menu_item" @click="appStore.closeAvatarMenu(); appStore.goPage('page-orders')">
                <span>📦</span> 我的订单
            </div>
            <div class="avatar_menu_item danger" @click="appStore.doLogout()">
                <span>🚪</span> 退出登录
            </div>
        </div>
    </header>
</template>

<script setup lang="ts">
const appStore = useAppStore();
const searchStore = useSearchStore();
const searchKeyword = ref('');
const avatarButtonRef = ref<HTMLElement | null>(null);
const avatarMenuRef = ref<HTMLElement | null>(null);

// 面包屑：页面 → 标题（纯展示映射，不影响路由逻辑）
const PAGE_TITLE_MAP: Record<string, string> = {
    'page-home': '首页',
    'page-search': '搜索',
    'page-office': 'Office',
    'page-activation': '激活码购买',
    'page-install': '安装 Office',
    'page-plugins': 'Ai 插件',
    'page-tips': '快捷技巧',
    'page-products': '全部商品',
    'page-purchase': '确认购买',
    'page-ai-lobster': 'Ai & 智能体',
    'page-ai-lobster-detail': '智能体详情',
    'page-orders': '我的订单',
    'page-plugins-mine': '已安装插件',
    'page-help': '帮助中心',
    'page-contact': '联系客服',
    'page-step2-activation': '激活账号绑定',
    'page-step3-install': '安装并开始使用',
    'page-activate-key': '密钥激活',
    'page-disk-clean': 'C盘清理',
    'page-image-viewer': '看图',
    'page-compress': '压缩',
    'page-pdf-tools': 'PDF 工具',
    'page-plugin-plaza': 'Ai 插件广场',
};
const breadcrumbTitle = computed(() => PAGE_TITLE_MAP[appStore.currentPage] || '首页');

function handleSearch(): void {
    searchStore.openSearch(searchKeyword.value);
}

// 点击头像外区域时，自动关闭账户菜单。
function onDocumentClick(event: MouseEvent): void {
    if (!appStore.avatarMenuVisible) {
        return;
    }

    const target = event.target as Node;
    const menu = avatarMenuRef.value;
    const avatarButton = avatarButtonRef.value;
    if (menu?.contains(target)) {
        return;
    }
    if (avatarButton && avatarButton.contains(target)) {
        return;
    }
    appStore.closeAvatarMenu();
}

onMounted(() => {
    document.addEventListener('click', onDocumentClick);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', onDocumentClick);
});
</script>

<style lang="less" scoped>
.header {
    height: var(--header-h);
    z-index: 100;
    flex-shrink: 0;
}

/* ── 主导航栏 ── */
.header_top {
    height: var(--header-top-h);
    background: linear-gradient(90deg, #8744C6 0%, #9D5ED0 25%, #B67CDC 58%, #CFA4EC 100%);
    padding: 0 16px;
    flex-shrink: 0;

    .draggable {
        -webkit-app-region: drag;
        width: calc(100% - 8px);
        height: 48px;
        top: 4px;
        left: 4px;
        z-index: 101;
    }
}

.header_logo {
    gap: 6px;
    flex-shrink: 0;
    user-select: none;
    position: relative;
    z-index: 102;

    .logo {
        width: 26px;
        height: 26px;
    }

    .logo_text {
        font-weight: 900;
        color: #fff;
    }
}

/* 搜索栏（紧贴右侧图标区） */
.hn_search_wrap {
    width: 200px;
    margin-left: auto;
    margin-right: 8px;
    flex-shrink: 0;
}

.hn_search_input {
    width: 100%;
    height: 30px;
    background: rgba(255, 255, 255, .18);
    border: 1px solid rgba(255, 255, 255, .35);
    border-radius: 15px;
    padding: 0 34px 0 14px;
    font-size: 12.5px;
    color: #fff;
    outline: none;
    transition: background .15s, border-color .15s;

    &::placeholder {
        color: rgba(255, 255, 255, .65);
    }

    &:focus {
        background: rgba(255, 255, 255, .28);
        border-color: rgba(255, 255, 255, .6);
    }
}

.hn_search_ico {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(255, 255, 255, .75);
    pointer-events: none;
}

/* 右侧 */
.header_right {
    gap: 4px;
    flex-shrink: 0;
    position: relative;
    z-index: 102;
}

.hn_login_btn {
    gap: 5px;
    padding: 5px 15px;
    font-size: 12.5px;
    border-radius: 20px;
    border: none;
    background: rgba(255, 255, 255, .88);
    color: #7C3AED;
    font-weight: 700;
    white-space: nowrap;
    box-shadow: 0 1px 8px rgba(0, 0, 0, .12);
    transition: background .15s, box-shadow .15s;

    &:hover {
        background: #fff;
        box-shadow: 0 2px 12px rgba(0, 0, 0, .18);
    }
}

.hn_avatar_wrap {
    gap: 5px;
    border-radius: 20px;
    padding: 2px 9px 2px 2px;
    transition: background .15s;

    &:hover {
        background: rgba(255, 255, 255, .22);
    }
}

.hn_avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, #E879F9, #6C4EF6);
    font-size: 12px;
    font-weight: 800;
    color: #fff;
    flex-shrink: 0;
    border: 2px solid rgba(255, 255, 255, .6);
    box-shadow: 0 1px 6px rgba(0, 0, 0, .18);
}

.hn_avatar_caret {
    font-size: 10px;
    color: rgba(255, 255, 255, .85);
    line-height: 1;
}

.hn_win_sep {
    width: 1px;
    height: 16px;
    background: rgba(255, 255, 255, .25);
    margin: 0 5px;
    flex-shrink: 0;
}

.hn_win_btn {
    width: 20px;
    height: 20px;
    border-radius: 5px;
    border: none;
    background: rgba(255, 255, 255, .12);
    flex-shrink: 0;
    transition: background .15s;

    &:hover {
        background: rgba(255, 255, 255, .26);
    }

    &.close:hover {
        background: rgba(220, 38, 38, .75);
    }

    .btn_icon {
        width: 14px;
        height: 14px;
    }
}

/* ── 副导航栏 ── */
.header_sub {
    height: var(--header-sub-h);
    background: rgba(255, 255, 255, .78);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-bottom: 1px solid rgba(255, 255, 255, .6);
    box-shadow: 0 2px 12px rgba(108, 78, 246, .07);
    padding: 0 20px;
    gap: 10px;
    flex-shrink: 0;
}

.header_breadcrumb {
    gap: 5px;
    font-size: 12.5px;
    color: #9B8EC4;
    flex: 1;
    min-width: 0;

    .hbc_root {
        color: #9B8EC4;
        transition: color .15s;

        &:hover {
            color: #6C4EF6;
        }
    }

    .hbc_sep {
        color: #C4B5E8;
        font-size: 10px;
    }

    .hbc_current {
        color: #1A1040;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

/* ── 账户下拉菜单 ── */
.avatar_menu {
    top: 58px;
    right: 16px;
    background: white;
    border: 1px solid var(--border);
    border-radius: 10px;
    box-shadow: 0 8px 28px rgba(0, 0, 0, .14);
    min-width: 196px;
    z-index: 200;
    opacity: 0;
    transform: translateY(-6px);
    pointer-events: none;
    transition: opacity .18s, transform .18s;

    &.show {
        opacity: 1;
        pointer-events: all;
        transform: translateY(0);
    }
}

.avatar_menu_header {
    padding: 14px 16px 10px;
    display: flex;
    align-items: center;
    gap: 10px;
    border-bottom: 1px solid var(--border);

    .wc_ico {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        color: white;
        border: 2px solid rgba(255, 255, 255, .75);
        background: linear-gradient(135deg, #E879F9, #6C4EF6);
    }

    .name {
        font-size: 14px;
        font-weight: 700;
        color: var(--text);
    }

    .sub {
        font-size: 11.5px;
        color: #07C160;
        font-weight: 600;
        margin-top: 1px;
    }
}

.avatar_menu_item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    font-size: 13px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: background .12s;

    &:hover {
        background: #F5F0FF;
        color: var(--text);
    }

    &.danger {
        color: #D13438;

        &:hover {
            background: #FFF0F0;
        }
    }
}
</style>
